import type { NewDocument, StaffDocument } from "@amazing-care/types";
import { type Query, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dataSource } from "@/lib/data-source";
import type { UploadProgress } from "@/lib/data-source/data-source.types";
import { queryKeys } from "@/lib/query-keys";

/**
 * Every document the user may read. The header's search loads it on every
 * page, so the department lists and detail pages below read from it rather
 * than asking the server again.
 */
export function useDocuments() {
  return useQuery({
    queryKey: queryKeys.documents,
    queryFn: () => dataSource.listDocuments(),
  });
}

export function useFeaturedDocuments() {
  return useQuery({
    queryKey: queryKeys.documents,
    queryFn: () => dataSource.listDocuments(),
    select: (documents) => documents.filter((document) => document.featured),
  });
}

export function useDepartmentDocuments(departmentId: string) {
  return useQuery({
    queryKey: queryKeys.documents,
    queryFn: () => dataSource.listDocuments(),
    select: (documents) => documents.filter((document) => document.departmentId === departmentId),
  });
}

export function useDocument(documentId: string, { enabled = true } = {}) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.document(documentId),
    queryFn: () => dataSource.getDocument(documentId),
    enabled,
    // Opened from a list, the document is already in the cache: show it at once
    // and let the fetch confirm it behind the scenes.
    placeholderData: () =>
      queryClient
        .getQueryData<StaffDocument[]>(queryKeys.documents)
        ?.find((document) => document.id === documentId),
  });
}

/** The stored file, for preview and download. Fetched only when asked for. */
export function useDocumentFile(documentId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.documentFile(documentId),
    queryFn: () => dataSource.getDocumentFile(documentId),
    enabled,
    // A file is replaced through useSaveDocument, which invalidates this key.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 1000 * 60,
  });
}

/** A Word, Excel or PowerPoint file as PDF, converted by SharePoint. Fetched only when shown. */
export function useDocumentPreview(documentId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.documentPreview(documentId),
    queryFn: () => dataSource.getDocumentPreview(documentId),
    enabled,
    // Lives under the file's key, so replacing the file invalidates it too.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 1000 * 60,
  });
}

/**
 * A link a video streams from. It lapses after an hour; the player asks for a
 * new one when it does. A refetch otherwise would restart the video, so the
 * link is never refreshed on its own.
 */
export function useDocumentLink(documentId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.documentLink(documentId),
    queryFn: () => dataSource.getDocumentLink(documentId),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 1000 * 60,
  });
}

/** Lists and details, but not stored files: those only change when replaced. */
function isDocumentMetadata(query: Query): boolean {
  return query.queryKey[2] !== "file";
}

export interface SaveDocumentInput {
  documentId?: string;
  input: NewDocument;
  file?: File;
  onProgress?: UploadProgress;
}

export function useSaveDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, input, file, onProgress }: SaveDocumentInput) =>
      documentId
        ? dataSource.updateDocument(documentId, input, file, onProgress)
        : dataSource.addDocument(input, file, onProgress),
    onSuccess: (_saved, { documentId, file }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.documents,
          predicate: isDocumentMetadata,
        }),
        // Only the edited document's file can have changed, and only if a new
        // one was uploaded; every other cached file stays put.
        documentId && file
          ? queryClient.invalidateQueries({ queryKey: queryKeys.documentFile(documentId) })
          : undefined,
        queryClient.invalidateQueries({ queryKey: queryKeys.activityLog }),
      ]),
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => dataSource.deleteDocument(documentId),
    onSuccess: (_result, documentId) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.documents,
          predicate: (query) => isDocumentMetadata(query) && query.queryKey[1] !== documentId,
        }),
        // Refetching the deleted document while its page is still open would
        // flash "not found" before the page navigates away. Marked stale
        // instead, so a later visit asks the server again.
        queryClient.invalidateQueries({
          queryKey: queryKeys.document(documentId),
          refetchType: "none",
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.activityLog }),
      ]),
  });
}
